import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, Star, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PropertyCard } from "@/components/PropertyCard";
import { GuestVoices } from "@/components/GuestVoices";
import { DateRangePicker } from "@/components/DateRangePicker";
import { GuestsPicker } from "@/components/GuestsPicker";
import { useApp } from "@/context/AppContext";
import { getProperties, getConfig, getZones } from "@/lib/api";
import { tr } from "@/i18n/translations";
import { Seo } from "@/lib/seo";

const PRICE_MIN = 40;
const PRICE_MAX = 450;

export default function Stays() {
  const { t, lang } = useApp();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [amenityList, setAmenityList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [zone, setZone] = useState(params.get("zone") || "all");
  const [type, setType] = useState(params.get("type") || "all");
  const [sort, setSort] = useState(params.get("sort") || "recommended");
  const [minStars, setMinStars] = useState(Number(params.get("min_stars")) || 0);
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price")) || PRICE_MAX);
  const [amenities, setAmenities] = useState(
    params.get("amenities") ? params.get("amenities").split(",") : []
  );
  const [ci, setCi] = useState(params.get("check_in") || "");
  const [co, setCo] = useState(params.get("check_out") || "");
  const [guests, setGuests] = useState(Number(params.get("guests")) || 2);
  const [childAges, setChildAges] = useState(
    params.get("children_ages") ? params.get("children_ages").split(",").map(Number) : []
  );
  const onlyDeals = params.get("deals") === "1";

  useEffect(() => {
    getConfig().then((c) => setAmenityList(c.amenities)).catch(() => {});
    getZones().then((z) => setZones(z.filter((x) => x.count > 0))).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProperties({
        zone,
        type,
        sort,
        min_stars: minStars || undefined,
        max_price: maxPrice < PRICE_MAX ? maxPrice : undefined,
        amenities: amenities.join(","),
        check_in: ci,
        check_out: co,
        guests,
        children_ages: childAges.join(","),
      });
      setItems(onlyDeals ? data.filter((p) => (p.savings || 0) > 0) : data);
    } finally {
      setLoading(false);
    }
  }, [zone, type, sort, minStars, maxPrice, amenities, ci, co, guests, childAges, onlyDeals]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (zone !== "all") p.set("zone", zone);
    if (type !== "all") p.set("type", type);
    if (sort !== "recommended") p.set("sort", sort);
    if (minStars) p.set("min_stars", minStars);
    if (maxPrice < PRICE_MAX) p.set("max_price", maxPrice);
    if (amenities.length) p.set("amenities", amenities.join(","));
    if (ci) p.set("check_in", ci);
    if (co) p.set("check_out", co);
    if (guests && (ci || co || childAges.length || guests != 2)) p.set("guests", guests);
    if (childAges.length) p.set("children_ages", childAges.join(","));
    if (onlyDeals) p.set("deals", "1");
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone, type, sort, minStars, maxPrice, amenities, ci, co, guests, childAges]);

  const toggleAmenity = (id) =>
    setAmenities((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const clearAll = () => {
    setZone("all");
    setType("all");
    setSort("recommended");
    setMinStars(0);
    setMaxPrice(PRICE_MAX);
    setAmenities([]);
    setCi("");
    setCo("");
    setGuests(2);
    setChildAges([]);
  };

  const today = new Date().toISOString().slice(0, 10);

  const filtersContent = (
    <div className="flex flex-col gap-7">
      <div>
        <p className="overline mb-3 text-petrol/60">{t("search.dates")}</p>
        <DateRangePicker
          ci={ci}
          co={co}
          onChange={(a, b) => {
            setCi(a);
            setCo(b);
          }}
          triggerClassName="flex w-full items-center gap-3 rounded-lg border border-sand-border bg-white px-3 py-2.5 text-left"
        />
      </div>
      <div>
        <p className="overline mb-3 text-petrol/60">{t("search.zone")}</p>
        <select
          data-testid="filter-zone"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          className="w-full rounded-lg border border-sand-border bg-white px-3 py-2.5 text-sm font-medium text-petrol outline-none focus:border-terracotta"
        >
          <option value="all">{t("search.all_zones")}</option>
          {zones.map((z) => (
            <option key={z.slug} value={z.slug}>
              {z.name} ({z.count})
            </option>
          ))}
        </select>
      </div>

      <div>
        <p className="overline mb-3 text-petrol/60">{t("search.guests")}</p>
        <GuestsPicker
          adults={guests}
          childrenAges={childAges}
          onChange={(a, kids) => { setGuests(a); setChildAges(kids); }}
          triggerClassName="flex w-full items-center gap-3 rounded-lg border border-sand-border bg-white px-3 py-2.5 text-left"
        />
      </div>

      <div>
        <p className="overline mb-3 text-petrol/60">{t("search.type")}</p>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "all", l: t("search.all_types") },
            { v: "hotel", l: t("search.hotel") },
            { v: "apartment", l: t("search.apartment") },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`filter-type-${o.v}`}
              onClick={() => setType(o.v)}
              className={`rounded-none border px-4 py-2 text-sm font-medium transition-colors ${
                type === o.v ? "border-terracotta bg-terracotta text-white" : "border-sand-border text-petrol hover:border-terracotta"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-3 text-petrol/60">
          {t("search.price_range")} · ≤ €{maxPrice}
        </p>
        <input
          type="range"
          data-testid="filter-price"
          min={PRICE_MIN}
          max={PRICE_MAX}
          step={10}
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full accent-terracotta"
        />
        <div className="mt-1 flex justify-between text-xs text-petrol/50">
          <span>€{PRICE_MIN}</span>
          <span>€{PRICE_MAX}+</span>
        </div>
      </div>

      <div>
        <p className="overline mb-3 text-petrol/60">{t("search.stars")}</p>
        <div className="flex gap-2">
          {[0, 3, 4, 5].map((s) => (
            <button
              key={s}
              data-testid={`filter-stars-${s}`}
              onClick={() => setMinStars(s)}
              className={`flex items-center gap-1 rounded-none border px-3 py-1.5 text-sm font-medium transition-colors ${
                minStars === s ? "border-terracotta bg-terracotta text-white" : "border-sand-border text-petrol"
              }`}
            >
              {s === 0 ? t("search.all_types") : <>{s}+ <Star size={12} className="fill-current" /></>}
            </button>
          ))}
        </div>
      </div>

      {amenityList.length > 0 && (
        <div>
          <p className="overline mb-3 text-petrol/60">{t("search.amenities")}</p>
          <div className="flex flex-col gap-2">
            {amenityList.map((a) => (
              <label key={a.id} className="flex cursor-pointer items-center gap-2.5 text-sm text-petrol">
                <input
                  type="checkbox"
                  data-testid={`filter-amenity-${a.id}`}
                  checked={amenities.includes(a.id)}
                  onChange={() => toggleAmenity(a.id)}
                  className="h-4 w-4 accent-terracotta"
                />
                {a[lang] || a.en}
              </label>
            ))}
          </div>
        </div>
      )}

      <button onClick={clearAll} className="flex items-center gap-2 text-sm font-semibold text-terracotta">
        <X size={15} /> {t("search.clear")}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand">
      <Seo
        title={
          zone !== "all"
            ? t("detail.seo_zone_title").replace("{zone}", zones.find((z) => z.slug === zone)?.name || zone)
            : t("detail.seo_stays_title")
        }
        description={
          zone !== "all"
            ? t("detail.seo_zone_desc").replace("{zone}", zones.find((z) => z.slug === zone)?.name || zone)
            : t("detail.seo_stays_desc")
        }
        url={`${window.location.origin}/stays${zone !== "all" ? `?zone=${zone}` : ""}`}
      />
      <Navbar solid />
      <div className="mx-auto max-w-7xl px-5 pb-20 pt-28 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-terracotta">{onlyDeals ? t("sections.deals_over") : "Umago · Istria"}</p>
            <h1 className="mt-1 font-serif text-4xl font-medium text-petrol lg:text-5xl">
              {loading ? (
                <span data-testid="results-count">…</span>
              ) : (
                <><span data-testid="results-count">{items.length}</span> {t("search.results")}</>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-none border border-sand-border bg-white px-4 py-2.5 text-sm font-medium text-petrol outline-none"
            >
              <option value="recommended">{t("search.sort_recommended")}</option>
              <option value="price_asc">{t("search.sort_price_asc")}</option>
              <option value="price_desc">{t("search.sort_price_desc")}</option>
              <option value="rating">{t("search.sort_rating")}</option>
              <option value="savings">{t("search.sort_savings")}</option>
            </select>
            <button
              onClick={() => setShowFilters((s) => !s)}
              data-testid="toggle-filters"
              className="flex items-center gap-2 rounded-none border border-sand-border bg-white px-4 py-2.5 text-sm font-medium text-petrol lg:hidden"
            >
              <SlidersHorizontal size={16} /> {t("search.filters")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
          <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <div className="sticky top-24 rounded-2xl border border-sand-border bg-sand-card p-6">
              {filtersContent}
            </div>
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-96 animate-pulse rounded-2xl bg-sand-card" />
                ))}
              </div>
            ) : items.length ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((p, i) => (
                  <PropertyCard key={p.slug} p={p} index={i} />
                ))}
              </div>
            ) : (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-24 text-center text-petrol/60">
                {t("search.no_results")}
              </motion.p>
            )}
          </div>
        </div>
      </div>
      {zone !== "all" && (
        <GuestVoices zone={zone} zoneName={zones.find((z) => z.slug === zone)?.name} />
      )}
      <Footer />
    </div>
  );
}
